import React from 'react';
import { Box, Checkbox, Chip, FormControlLabel, InputAdornment, TextField, Typography, Paper, Tooltip, Select, MenuItem, FormLabel, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Button } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import InfoTooltip from '../../InfoTooltip';
import { useBlueprintContext } from '../../../context/BlueprintContext';
import { featureRequiredVersion } from '../../../blueprints';


interface PatchContentProps {
  patchOptions: PatchOptionsState;
  setPatchOptions: (opts: PatchOptionsState) => void;
  patchFolder: string;
  setPatchFolder: (v: string) => void;
  patchFolderPlaceholder: string;
  executionMode?: string;
  targetVersionEnabled?: boolean;
  targetVersion?: string;
  platform: string;
  download?: 'NO' | 'YES';
  onSwitchToDownloadMode?: () => void;
  onNavigateToPatchTab?: () => void;
}

export interface PatchOptionsState {
  recommended: boolean;
  ru: boolean;
  ruVersion: string;
  ojvm: boolean;
  ojvmVersion: string;
  opatch: boolean;
  dpbp: boolean;
  au?: boolean;
  ahf?: boolean;
  cvu?: boolean;
  sqlcl?: boolean;
  tools?: boolean;
  jdk: boolean;
  specificPatches: string;
  mrp?: boolean;
  cspu?: boolean;
  ocw?: boolean;
  ocwVersion: string;
  cpat?: boolean;
  dbsat?: boolean;
  exaqfsdp?: boolean;
  exapatchmgr?: boolean;
  oem?: boolean;
  oemVersion?: string;
  gi?: boolean;
  sdo?: boolean;
  text?: boolean;
  sourceHome?: boolean;
  goldImage?: 'NO' | 'YES' | 'ALL' | 'AUTO';
  recommendedVersion?: string;
  useGoldImageFile?: boolean;
  goldImageFile?: string;
  createGoldImage?: string;
  goldImageSecurityLevel?: 'CRITICAL' | 'HIGH' | 'MEDIUM' | 'LOW';
}

const PatchContent: React.FC<PatchContentProps & { patchKey?: string }> = ({ patchOptions, setPatchOptions, patchFolder, setPatchFolder, patchFolderPlaceholder, executionMode, targetVersionEnabled, targetVersion, patchKey, platform, download, onSwitchToDownloadMode, onNavigateToPatchTab }) => {
  const { feat } = useBlueprintContext();
  const [specificPatchesTouched, setSpecificPatchesTouched] = React.useState(false);
  const [patchFolderTouched, setPatchFolderTouched] = React.useState(false);
  const goldImageInputRef = React.useRef<HTMLInputElement>(null);
  const [downloadModeConfirm, setDownloadModeConfirm] = React.useState<'tools' | 'ahf' | 'cvu' | 'sqlcl' | 'cpat' | 'dbsat' | 'exaqfsdp' | 'exapatchmgr' | 'oem' | 'gi' | null>(null);

  const handleChange = (field: keyof PatchOptionsState) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setPatchOptions({ ...patchOptions, [field]: e.target.checked });
  };
  const handleInput = (field: keyof PatchOptionsState) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setPatchOptions({ ...patchOptions, [field]: e.target.value });
  };
  const oemSelected = feat('patch.oem') && !!patchOptions.oem;
  const giSelected = feat('patch.gi') && !!patchOptions.gi;
  const allDisabled = patchOptions.recommended || !!patchOptions.useGoldImageFile || oemSelected || giSelected;
  const giAllowedWhenSelected = (field: string) => giSelected && (field === 'mrp' || field === 'opatch' || field === 'patch_number');

  const isDownloadOrCreateHome = executionMode === 'download' || executionMode === 'create_home';
  // Suggest version placeholder based on selected target_version across all modes
  const optionalVersionPlaceholder = (() => {
    if (!targetVersionEnabled) return '19.31';
    const v = String(targetVersion ?? '19').trim();
    if (v === '19') return '19.31';
    if (v === '21') return '21.22';
    if (v === '23' || v === '26') return '23.26.2';
    return '19.31';
  })();
  const ojvmEmbedded = !!targetVersionEnabled && (targetVersion === '21' || targetVersion === '23' || targetVersion === '26');
  const sdoDisabled = !!targetVersionEnabled && (targetVersion === '21' || targetVersion === '23' || targetVersion === '26');
  const textDisabled = !!targetVersionEnabled && (targetVersion === '21' || targetVersion === '23' || targetVersion === '26');
  // Validation for RU/OJVM version in Download or Create Home Mode
  const ruVersionInvalid = patchOptions.ru && isDownloadOrCreateHome && patchOptions.ruVersion && !/^(19|21|23|26)(\.|$)/.test(patchOptions.ruVersion);
  const ojvmVersionInvalid = patchOptions.ojvm && isDownloadOrCreateHome && patchOptions.ojvmVersion && !/^(19|21|23|26)(\.|$)/.test(patchOptions.ojvmVersion);
  const recommendedVersionInvalid = patchOptions.recommended && isDownloadOrCreateHome && patchOptions.recommendedVersion && !/^(19|21|23|26)(\.|$)/.test(patchOptions.recommendedVersion);

  // Improved Validation for MRP + RU version < 19.17 (all modes)
  let mrpVersionWarning = false;
  let ruMinor = null;
  if (patchOptions.ru && patchOptions.ruVersion) {
    const match = patchOptions.ruVersion.match(/^19\.(\d+)/);
    if (match) {
      ruMinor = Number(match[1]);
    }
  }
  if (patchOptions.mrp && ruMinor !== null && ruMinor < 17) {
    mrpVersionWarning = true;
  }

  // Determine if MRP should be shown
  const showMRP =
    (executionMode === 'download' && platform === 'LINUX.X64') ||
    (['deploy', 'create_home', 'fixups', 'analyze'].includes(executionMode || '') && platform !== 'WINDOWS.X64');

  // CSPU contextual tooltip/subtext based on platform + target version
  const effectiveVersion = targetVersionEnabled ? (targetVersion ?? '19') : '19';
  const isLinuxPlatform = platform === 'LINUX.X64';
  const isWindowsPlatform = platform === 'WINDOWS.X64';
  // Show CSPU only where it exists as a separate patch (not bundled in MRP)
  const showCSPU = (() => {
    if (effectiveVersion === '19') return !isLinuxPlatform; // Linux: bundled in MRP → hidden; non-Linux: coming soon
    if (effectiveVersion === '21') return isLinuxPlatform || isWindowsPlatform; // 21c has separate CSPU on Linux/Windows
    return false; // 26ai Linux: bundled in MRP; 26ai non-Linux: not available; 23c: n/a
  })();
  // 21c CSPU is now supported on Linux/Windows (AUPG-6729)
  const cspuInteractive = effectiveVersion === '21' && (isLinuxPlatform || isWindowsPlatform);
  const cspuTooltip = (() => {
    if (effectiveVersion === '19') {
      if (isLinuxPlatform) return 'On Linux x86-64 for Oracle 19c, CSPU security fixes are bundled inside MRP — select MRP instead of CSPU.';
      return 'CSPU availability varies by month and RU version. Check My Oracle Support for the latest available CSPU for your platform.';
    }
    if (effectiveVersion === '21') return 'Cumulative Security Patch Update — supported on Oracle 21c Linux x86-64 and Windows x86-64.';
    if (effectiveVersion === '26') return 'On Linux x86-64 for Oracle 26ai, CSPU security fixes are bundled inside MRP — select MRP instead of CSPU.';
    return '';
  })();
  const cspuSubtext = (() => {
    if (effectiveVersion === '19') return isLinuxPlatform ? 'Included in MRP on Linux' : 'Coming soon';
    if (effectiveVersion === '26') return 'Included in MRP on Linux';
    return '';
  })();

  const showRuTooltip = (isDownloadOrCreateHome && !patchOptions.sourceHome && !targetVersionEnabled);

  return (
    <Paper elevation={0} sx={{ p: 3, background: 'background.paper', borderRadius: 3 }}>
      <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            flexWrap: 'wrap',
            minWidth: 0,
            background: theme => theme.palette.mode === 'dark' ? '#333' : '#e3f2fd',
            color: theme => theme.palette.mode === 'dark' ? '#b2ebf2' : '#1976d2',
            borderRadius: 2,
            px: 2,
            py: 1,
            fontWeight: 500,
            fontSize: 15,
            maxWidth: '100%',
            gap: 1.5,
          }}
        >
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 0, flex: 1 }}>
            If no version is specified below, the latest available will be used automatically.
          </span>
        </Box>
      </Box>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, columnGap: 4 }}>
        <Box sx={{ flex: 1, minWidth: 200 }}>
          <FormControlLabel
            control={<Checkbox checked={!oemSelected && patchOptions.recommended} onChange={handleChange('recommended')} disabled={!!patchOptions.useGoldImageFile || oemSelected || giSelected} />}
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography sx={{ fontWeight: 700 }}>RECOMMENDED</Typography>
                <InfoTooltip
                  title={
                    <>AutoUpgrade will consider all of the following options: <b>RU, OPATCH, OJVM, DPBP</b>. DPBP is skipped automatically when not available for the selected RU.</>
                  }
                  placement="top"
                  disabled={!!patchOptions.useGoldImageFile || oemSelected || giSelected}
                />
                {patchOptions.recommended && feat('patch.recommended_version') && (
                  <>
                    <TextField
                      size="small"
                      placeholder={`Optional: ${optionalVersionPlaceholder}`}
                      value={patchOptions.recommendedVersion || ''}
                      onChange={e => setPatchOptions({ ...patchOptions, recommendedVersion: e.target.value })}
                      sx={{ width: 160 }}
                      error={!!recommendedVersionInvalid}
                    />
                    {recommendedVersionInvalid && (
                      <Typography sx={{ color: 'error.main', fontSize: 13, whiteSpace: 'nowrap' }}>
                        Only 19.x/21.x/23.x/26.x allowed in Create Home / Download
                      </Typography>
                    )}
                  </>
                )}
              </Box>
            }
          />
        </Box>
        <Box sx={{ flex: 1, minWidth: 200 }}>
          {feat('patch.tools') ? (
            <FormControlLabel
              control={
                <Checkbox
                  checked={!oemSelected && !!patchOptions.tools}
                  onChange={e => {
                    if (e.target.checked && executionMode !== 'download') {
                      setDownloadModeConfirm('tools');
                    } else {
                      setPatchOptions({ ...patchOptions, tools: e.target.checked });
                    }
                  }}
                  disabled={allDisabled}
                />
              }
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography sx={{ fontWeight: 700 }}>TOOLS</Typography>
                  <InfoTooltip
                    title={<>AutoUpgrade will consider all of the following options: <b>OPATCH, AU, AHF, CVU</b> and <b>SQLcl</b>. Available from AutoUpgrade 26.3+.</>}
                    placement="top"
                    disabled={allDisabled}
                  />
                </Box>
              }
            />
          ) : (
            <Tooltip
              title={`TOOLS (OPATCH + AU + AHF + CVU + SQLcl combined) — only available from AutoUpgrade ${featureRequiredVersion('patch.tools')}+`}
              placement="right"
              arrow
              componentsProps={{ tooltip: { sx: { bgcolor: theme => theme.palette.mode === 'dark' ? '#f5f5f5' : '#23272f', color: theme => theme.palette.mode === 'dark' ? '#222' : '#fff', fontSize: 15, borderRadius: 2, boxShadow: 3, px: 2, py: 1, maxWidth: 360 } } }}
            >
              <Box sx={{ opacity: 0.38, cursor: 'not-allowed', display: 'inline-flex', alignItems: 'center', width: 'fit-content' }}>
                <Box sx={{ pointerEvents: 'none' }}>
                  <FormControlLabel control={<Checkbox checked={false} disabled />} label={<Typography component="span" sx={{ fontWeight: 700 }}>TOOLS</Typography>} />
                </Box>
              </Box>
            </Tooltip>
          )}
        </Box>
      </Box>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, columnGap: 4, alignItems: 'flex-start', mt: 1 }}>
        {/* Left column: RU, MRP, DPBP, JDK, OJVM, OCW, SDO */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, minWidth: 200, flex: 1 }}>
          <Box>
            <FormControlLabel
              control={<Checkbox checked={!oemSelected && (patchOptions.recommended || patchOptions.ru)} onChange={handleChange('ru')} disabled={allDisabled} />}
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', ...(allDisabled && { color: 'text.disabled' }) }}>
                  {showRuTooltip ? (
                    <Tooltip
                      title="If Source Home or Target Version are not selected, specify a RU using RU:x.y notation where AutoUpgrade patching can derive the target version."
                      placement="right"
                      arrow
                      componentsProps={{
                        tooltip: {
                          sx: {
                            bgcolor: theme => theme.palette.mode === 'dark' ? '#f5f5f5' : '#23272f',
                            color: theme => theme.palette.mode === 'dark' ? '#222' : '#fff',
                            fontSize: 15,
                            borderRadius: 2,
                            boxShadow: 3,
                            px: 2,
                            py: 1,
                            fontWeight: 400,
                            minWidth: 320,
                          },
                        },
                      }}
                    >
                      <span>
                        <Typography sx={{ fontWeight: 600, mr: 1, display: 'inline' }}>RU</Typography>
                      </span>
                    </Tooltip>
                  ) : (
                    <Tooltip
                      title="Database Release Update"
                      placement="right"
                      arrow
                      componentsProps={{
                        tooltip: {
                          sx: {
                            bgcolor: theme => theme.palette.mode === 'dark' ? '#f5f5f5' : '#23272f',
                            color: theme => theme.palette.mode === 'dark' ? '#222' : '#fff',
                            fontSize: 15,
                            borderRadius: 2,
                            boxShadow: 3,
                            px: 2,
                            py: 1,
                            fontWeight: 400,
                            minWidth: 220,
                          },
                        },
                      }}
                    >
                      <Typography component="span" sx={{ fontWeight: 600, mr: 1 }}>RU</Typography>
                    </Tooltip>
                  )}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    {isDownloadOrCreateHome ? (
                      <Tooltip
                        title="This may be required if Source Home or Target Version are not specified."
                        placement="right"
                        arrow
                        componentsProps={{
                          tooltip: {
                            sx: {
                              bgcolor: theme => theme.palette.mode === 'dark' ? '#f5f5f5' : '#23272f',
                              color: theme => theme.palette.mode === 'dark' ? '#222' : '#fff',
                              fontSize: 15,
                              borderRadius: 2,
                              boxShadow: 3,
                              px: 2,
                              py: 1,
                              fontWeight: 400,
                              minWidth: 320,
                            },
                          },
                        }}
                      >
                        <TextField
                          size="small"
                          placeholder={`Optional: ${optionalVersionPlaceholder}`}
                          value={patchOptions.ruVersion}
                          onChange={handleInput('ruVersion')}
                          disabled={allDisabled}
                          sx={{ width: 160 }}
                          error={!!ruVersionInvalid}
                        />
                      </Tooltip>
                    ) : (
                      <TextField
                        size="small"
                        placeholder={`Optional: ${optionalVersionPlaceholder}`}
                        value={patchOptions.ruVersion}
                        onChange={handleInput('ruVersion')}
                        disabled={allDisabled}
                        sx={{ width: 160 }}
                        error={!!ruVersionInvalid}
                      />
                    )}
                    {ruVersionInvalid && (
                      <Typography sx={{ color: 'error.main', fontSize: 14, whiteSpace: 'nowrap' }}>
                        Only 19.x/21.x/23.x/26.x versions are allowed in Create Home and Download Modes
                      </Typography>
                    )}
                  </Box>
                </Box>
              }
            />
          </Box>
          {showMRP && (
            <Box>
              <FormControlLabel
                control={<Checkbox checked={!oemSelected && !!patchOptions.mrp} onChange={handleChange('mrp')} disabled={allDisabled && !giAllowedWhenSelected('mrp')} />}
                label={
                  <Tooltip
                    title="Monthly Recommended Patches"
                    placement="right"
                    arrow
                    componentsProps={{
                      tooltip: {
                        sx: {
                          bgcolor: theme => theme.palette.mode === 'dark' ? '#f5f5f5' : '#23272f',
                          color: theme => theme.palette.mode === 'dark' ? '#222' : '#fff',
                          fontSize: 15,
                          borderRadius: 2,
                          boxShadow: 3,
                          px: 2,
                          py: 1,
                          fontWeight: 400,
                          minWidth: 220,
                        },
                      },
                    }}
                  >
                    <Typography component="span" sx={{ fontWeight: 600 }}>MRP</Typography>
                  </Tooltip>
                }
              />
            </Box>
          )}
          {showCSPU && (
            <Box>
              <FormControlLabel
                control={
                  cspuInteractive
                    ? <Checkbox checked={!oemSelected && !!patchOptions.cspu} onChange={handleChange('cspu')} disabled={allDisabled} />
                    : <Checkbox checked={false} disabled />
                }
                label={
                  <Tooltip
                    title={cspuTooltip}
                    placement="right"
                    arrow
                    componentsProps={{
                      tooltip: {
                        sx: {
                          bgcolor: theme => theme.palette.mode === 'dark' ? '#f5f5f5' : '#23272f',
                          color: theme => theme.palette.mode === 'dark' ? '#222' : '#fff',
                          fontSize: 15,
                          borderRadius: 2,
                          boxShadow: 3,
                          px: 2,
                          py: 1,
                          fontWeight: 400,
                          minWidth: 280,
                        },
                      },
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography component="span" sx={{ fontWeight: 600, ...(!cspuInteractive && { color: 'text.disabled' }) }}>CSPU</Typography>
                      {cspuSubtext && (
                        <Typography sx={{ fontSize: 13, color: 'text.disabled', fontStyle: 'italic' }}>
                          {cspuSubtext}
                        </Typography>
                      )}
                    </Box>
                  </Tooltip>
                }
              />
            </Box>
          )}
          <Box>
            <FormControlLabel
              control={<Checkbox checked={!oemSelected && (patchOptions.recommended || patchOptions.dpbp)} onChange={handleChange('dpbp')} disabled={allDisabled} />}
              label={
                <Tooltip
                  title="Data Pump Bundle Patch"
                  placement="right"
                  arrow
                  componentsProps={{
                    tooltip: {
                      sx: {
                        bgcolor: theme => theme.palette.mode === 'dark' ? '#f5f5f5' : '#23272f',
                        color: theme => theme.palette.mode === 'dark' ? '#222' : '#fff',
                        fontSize: 15,
                        borderRadius: 2,
                        boxShadow: 3,
                        px: 2,
                        py: 1,
                        fontWeight: 400,
                        minWidth: 220,
                      },
                    },
                  }}
                >
                  <Typography component="span" sx={{ fontWeight: 600, ...(allDisabled && { color: 'text.disabled' }) }}>DPBP</Typography>
                </Tooltip>
              }
            />
          </Box>
          <Box>
            <FormControlLabel
              control={<Checkbox checked={!oemSelected && patchOptions.jdk} onChange={handleChange('jdk')} disabled={allDisabled} />}
              label={
                <Tooltip
                  title="Database Client JDK"
                  placement="right"
                  arrow
                  componentsProps={{
                    tooltip: {
                      sx: {
                        bgcolor: theme => theme.palette.mode === 'dark' ? '#f5f5f5' : '#23272f',
                        color: theme => theme.palette.mode === 'dark' ? '#222' : '#fff',
                        fontSize: 15,
                        borderRadius: 2,
                        boxShadow: 3,
                        px: 2,
                        py: 1,
                        fontWeight: 400,
                        minWidth: 220,
                      },
                    },
                  }}
                >
                  <Typography component="span" sx={{ fontWeight: 600 }}>JDK</Typography>
                </Tooltip>
              }
            />
          </Box>
          <Box>
            <FormControlLabel
              control={<Checkbox checked={!oemSelected && (patchOptions.recommended || patchOptions.ojvm)} onChange={handleChange('ojvm')} disabled={allDisabled || ojvmEmbedded} />}
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', ...((allDisabled || ojvmEmbedded) && { color: 'text.disabled' }) }}>
                  <Tooltip
                    title="Oracle JavaVM Component Release Update"
                    placement="right"
                    arrow
                    componentsProps={{
                      tooltip: {
                        sx: {
                          bgcolor: theme => theme.palette.mode === 'dark' ? '#f5f5f5' : '#23272f',
                          color: theme => theme.palette.mode === 'dark' ? '#222' : '#fff',
                          fontSize: 15,
                          borderRadius: 2,
                          boxShadow: 3,
                          px: 2,
                          py: 1,
                          fontWeight: 400,
                          minWidth: 220,
                        },
                      },
                    }}
                  >
                    <Typography component="span" sx={{ fontWeight: 600, mr: 1 }}>OJVM</Typography>
                  </Tooltip>
                  {ojvmEmbedded ? (
                    <Typography sx={{ fontSize: 13, color: 'text.disabled', fontStyle: 'italic' }}>
                      OJVM is already included in Release Update
                    </Typography>
                  ) : (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <TextField
                        size="small"
                        placeholder={`Optional: ${optionalVersionPlaceholder}`}
                        value={patchOptions.ojvmVersion}
                        onChange={handleInput('ojvmVersion')}
                        disabled={allDisabled}
                        sx={{ width: 160 }}
                        error={!!ojvmVersionInvalid}
                      />
                      {ojvmVersionInvalid && (
                        <Typography sx={{ color: 'error.main', fontSize: 14, whiteSpace: 'nowrap' }}>
                          Only 19.x/21.x/23.x/26.x versions are allowed in Create Home and Download Modes
                        </Typography>
                      )}
                    </Box>
                  )}
                </Box>
              }
            />
          </Box>
          <Box>
            <FormControlLabel
              control={<Checkbox checked={!oemSelected && !giSelected && !!patchOptions.ocw} onChange={handleChange('ocw')} disabled={allDisabled} />}
              label={
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Tooltip
                    title="Grid Infrastructure Release Update (OCW keyword)"
                    placement="right"
                    arrow
                    componentsProps={{
                      tooltip: {
                        sx: {
                          bgcolor: theme => theme.palette.mode === 'dark' ? '#f5f5f5' : '#23272f',
                          color: theme => theme.palette.mode === 'dark' ? '#222' : '#fff',
                          fontSize: 15,
                          borderRadius: 2,
                          boxShadow: 3,
                          px: 2,
                          py: 1,
                          fontWeight: 400,
                          minWidth: 220,
                        },
                      },
                    }}
                  >
                    <Typography component="span" sx={{ fontWeight: 600, mr: 1 }}>OCW</Typography>
                  </Tooltip>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <TextField
                      size="small"
                      placeholder={`Optional: ${optionalVersionPlaceholder}`}
                      value={patchOptions.ocwVersion}
                      onChange={handleInput('ocwVersion')}
                      disabled={allDisabled}
                      sx={{ width: 160 }}
                    />
                  </Box>
                </Box>
              }
            />
          </Box>
          {/* CPAT — 26.6+, download-only */}
          {feat('patch.cpat') && (
            <Box>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={!oemSelected && !!patchOptions.cpat}
                    disabled={allDisabled}
                    onChange={e => {
                      if (e.target.checked && executionMode !== 'download') {
                        setDownloadModeConfirm('cpat');
                      } else {
                        setPatchOptions({ ...patchOptions, cpat: e.target.checked });
                      }
                    }}
                  />
                }
                label={
                  <Tooltip
                    title={executionMode !== 'download' ? 'Cloud Premigration Advisor Tool — requires Download mode. Click to switch.' : 'Cloud Premigration Advisor Tool. AutoUpgrade 26.6+'}
                    placement="right" arrow
                    componentsProps={{ tooltip: { sx: { bgcolor: theme => theme.palette.mode === 'dark' ? '#f5f5f5' : '#23272f', color: theme => theme.palette.mode === 'dark' ? '#222' : '#fff', fontSize: 15, borderRadius: 2, boxShadow: 3, px: 2, py: 1, maxWidth: 300 } } }}
                  >
                    <Typography component="span" sx={{ fontWeight: 600 }}>CPAT</Typography>
                  </Tooltip>
                }
              />
            </Box>
          )}
          {/* EXAQFSDP — 26.6+, download-only; emits EXAQFSDP:<targetVersion> */}
          {feat('patch.exaqfsdp') && (
            <Box>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={!oemSelected && !!patchOptions.exaqfsdp}
                    disabled={allDisabled}
                    onChange={e => {
                      if (e.target.checked && executionMode !== 'download') {
                        setDownloadModeConfirm('exaqfsdp');
                      } else {
                        setPatchOptions({ ...patchOptions, exaqfsdp: e.target.checked });
                      }
                    }}
                  />
                }
                label={
                  <Tooltip
                    title={executionMode !== 'download' ? 'Exadata Quarterly Full Stack Download Patch — requires Download mode. Click to switch.' : 'Exadata Quarterly Full Stack Download Patch. Version derived from target_version. AutoUpgrade 26.6+'}
                    placement="right" arrow
                    componentsProps={{ tooltip: { sx: { bgcolor: theme => theme.palette.mode === 'dark' ? '#f5f5f5' : '#23272f', color: theme => theme.palette.mode === 'dark' ? '#222' : '#fff', fontSize: 15, borderRadius: 2, boxShadow: 3, px: 2, py: 1, maxWidth: 340 } } }}
                  >
                    <Typography component="span" sx={{ fontWeight: 600 }}>EXAQFSDP</Typography>
                  </Tooltip>
                }
              />
            </Box>
          )}
          {/* SDO — moved here to balance columns */}
          <FormControlLabel
            control={<Checkbox checked={!oemSelected && !!patchOptions.sdo} onChange={handleChange('sdo')} disabled={sdoDisabled || allDisabled} />}
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', ...(sdoDisabled && { color: 'text.disabled' }) }}>
                <Tooltip
                  title="Spatial Bundle Patch"
                  placement="right"
                  arrow
                  componentsProps={{ tooltip: { sx: { bgcolor: theme => theme.palette.mode === 'dark' ? '#f5f5f5' : '#23272f', color: theme => theme.palette.mode === 'dark' ? '#222' : '#fff', fontSize: 15, borderRadius: 2, boxShadow: 3, px: 2, py: 1, fontWeight: 400, minWidth: 220 } } }}
                >
                  <Typography component="span" sx={{ fontWeight: 600, mr: 1 }}>SDO</Typography>
                </Tooltip>
                {sdoDisabled && (
                  <Typography sx={{ fontSize: 13, color: 'text.disabled', fontStyle: 'italic' }}>
                    Temporarily disabled for 21c/23c/26ai — It will be released on AutoUpgrade version 26.4
                  </Typography>
                )}
              </Box>
            }
          />
          {/* GI — 26.6+, download-only */}
          {feat('patch.gi') && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={!!patchOptions.gi}
                    disabled={patchOptions.recommended || !!patchOptions.useGoldImageFile || oemSelected}
                    onChange={e => {
                      if (e.target.checked && executionMode !== 'download') {
                        setDownloadModeConfirm('gi');
                      } else if (e.target.checked) {
                        setPatchOptions({
                          ...patchOptions, gi: true,
                          ru: false, ojvm: false, dpbp: false, ocw: false, cspu: false,
                          cpat: false, dbsat: false, exaqfsdp: false, exapatchmgr: false,
                          oem: false, oemVersion: '',
                          text: false, au: false, ahf: false, cvu: false,
                          sqlcl: false, tools: false, sdo: false, jdk: false,
                          recommended: false,
                        });
                      } else {
                        setPatchOptions({ ...patchOptions, gi: false });
                      }
                    }}
                  />
                }
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Tooltip
                      title={executionMode !== 'download' ? 'Grid Infrastructure — requires Download mode. Can combine with MRP and OPATCH. Click to switch.' : 'Grid Infrastructure download. Can combine with MRP and OPATCH. AutoUpgrade 26.6+'}
                      placement="right" arrow
                      componentsProps={{ tooltip: { sx: { bgcolor: theme => theme.palette.mode === 'dark' ? '#f5f5f5' : '#23272f', color: theme => theme.palette.mode === 'dark' ? '#222' : '#fff', fontSize: 15, borderRadius: 2, boxShadow: 3, px: 2, py: 1, maxWidth: 360 } } }}
                    >
                      <Typography component="span" sx={{ fontWeight: 600 }}>GI</Typography>
                    </Tooltip>
                    <InfoTooltip
                      title={<>Grid Infrastructure download — can combine with MRP and OPATCH. Download mode only. AutoUpgrade 26.6+.</>}
                      placement="right"
                      disabled={patchOptions.recommended || !!patchOptions.useGoldImageFile || oemSelected}
                    />
                  </Box>
                }
              />
            </Box>
          )}
        </Box>
        {/* Right column: TEXT, OPATCH, AU, AHF, CVU, SQLCL */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, minWidth: 200, flex: 1 }}>
          <Box>
            <FormControlLabel
              control={<Checkbox checked={!oemSelected && !!patchOptions.text} onChange={handleChange('text')} disabled={textDisabled || allDisabled} />}
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', ...(textDisabled && { color: 'text.disabled' }) }}>
                  <Tooltip
                    title="Oracle Text Patch"
                    placement="right"
                    arrow
                    componentsProps={{
                      tooltip: {
                        sx: {
                          bgcolor: theme => theme.palette.mode === 'dark' ? '#f5f5f5' : '#23272f',
                          color: theme => theme.palette.mode === 'dark' ? '#222' : '#fff',
                          fontSize: 15,
                          borderRadius: 2,
                          boxShadow: 3,
                          px: 2,
                          py: 1,
                          fontWeight: 400,
                          minWidth: 220,
                        },
                      },
                    }}
                  >
                    <Typography component="span" sx={{ fontWeight: 600, mr: 1 }}>TEXT</Typography>
                  </Tooltip>
                  {textDisabled && (
                    <Typography sx={{ fontSize: 13, color: 'text.disabled', fontStyle: 'italic' }}>
                      Oracle Text content is fully embedded in the Release Update starting on 21c — no separate TEXT patch is available for these versions. For Oracle 19c, a separate TEXT MLR can be applied on top of the Release Update
                    </Typography>
                  )}
                </Box>
              }
            />
          </Box>
          <FormControlLabel
            control={
              <Checkbox
                checked={!oemSelected && (!!patchOptions.tools || (allDisabled && !giAllowedWhenSelected('opatch')) || !!patchOptions.opatch)}
                onChange={handleChange('opatch')}
                disabled={!!patchOptions.tools || (allDisabled && !giAllowedWhenSelected('opatch'))}
              />
            }
            label={
              <Tooltip
                title="Oracle Patch Utility — downloads the latest OPatch version from MOS. Required for applying patches to the Oracle Home."
                placement="right"
                arrow
                componentsProps={{
                  tooltip: {
                    sx: {
                      bgcolor: theme => theme.palette.mode === 'dark' ? '#f5f5f5' : '#23272f',
                      color: theme => theme.palette.mode === 'dark' ? '#222' : '#fff',
                      fontSize: 15,
                      borderRadius: 2,
                      boxShadow: 3,
                      px: 2,
                      py: 1,
                      maxWidth: 380,
                    },
                  },
                }}
              >
                <Typography
                  component="span"
                  sx={{ fontWeight: 600, ...((!!patchOptions.tools || (allDisabled && !giAllowedWhenSelected('opatch'))) && { color: 'text.disabled' }) }}
                >
                  OPATCH
                </Typography>
              </Tooltip>
            }
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={!oemSelected && (!!patchOptions.tools || !!patchOptions.au)}
                onChange={handleChange('au')}
                disabled={!!patchOptions.tools || allDisabled}
              />
            }
            label={
              <Tooltip
                title="AutoUpgrade"
                placement="right"
                arrow
                componentsProps={{
                  tooltip: {
                    sx: {
                      bgcolor: theme => theme.palette.mode === 'dark' ? '#f5f5f5' : '#23272f',
                      color: theme => theme.palette.mode === 'dark' ? '#222' : '#fff',
                      fontSize: 15,
                      borderRadius: 2,
                      boxShadow: 3,
                      px: 2,
                      py: 1,
                      maxWidth: 380,
                    },
                  },
                }}
              >
                <Typography
                  component="span"
                  sx={{ fontWeight: 600, ...(!!patchOptions.tools && { color: 'text.disabled' }) }}
                >
                  AU
                </Typography>
              </Tooltip>
            }
          />
          {feat('patch.ahf') ? (
            <FormControlLabel
              control={<Checkbox checked={!oemSelected && (!!patchOptions.tools || !!patchOptions.ahf)} onChange={e => { if (e.target.checked && executionMode !== 'download') { setDownloadModeConfirm('ahf'); } else { setPatchOptions({ ...patchOptions, ahf: e.target.checked }); } }} disabled={!!patchOptions.tools || allDisabled} />}
              label={
                <Tooltip title="Autonomous Health Framework — Available from AutoUpgrade 26.3+" placement="right" arrow componentsProps={{ tooltip: { sx: { bgcolor: theme => theme.palette.mode === 'dark' ? '#f5f5f5' : '#23272f', color: theme => theme.palette.mode === 'dark' ? '#222' : '#fff', fontSize: 15, borderRadius: 2, boxShadow: 3, px: 2, py: 1 } } }}>
                  <Typography component="span" sx={{ fontWeight: 600, ...(!!patchOptions.tools && { color: 'text.disabled' }) }}>AHF</Typography>
                </Tooltip>
              }
            />
          ) : (
            <Tooltip
              title={`Autonomous Health Framework — only available from AutoUpgrade ${featureRequiredVersion('patch.ahf')}+`}
              placement="right"
              arrow
              componentsProps={{ tooltip: { sx: { bgcolor: theme => theme.palette.mode === 'dark' ? '#f5f5f5' : '#23272f', color: theme => theme.palette.mode === 'dark' ? '#222' : '#fff', fontSize: 15, borderRadius: 2, boxShadow: 3, px: 2, py: 1, maxWidth: 320 } } }}
            >
              <Box sx={{ opacity: 0.38, cursor: 'not-allowed', display: 'inline-flex', alignItems: 'center', width: 'fit-content' }}>
                <Box sx={{ pointerEvents: 'none' }}>
                  <FormControlLabel control={<Checkbox checked={false} disabled />} label={<Typography component="span" sx={{ fontWeight: 600 }}>AHF</Typography>} />
                </Box>
              </Box>
            </Tooltip>
          )}
          {feat('patch.cvu') ? (
            <FormControlLabel
              control={<Checkbox checked={!oemSelected && (!!patchOptions.tools || !!patchOptions.cvu)} onChange={e => { if (e.target.checked && executionMode !== 'download') { setDownloadModeConfirm('cvu'); } else { setPatchOptions({ ...patchOptions, cvu: e.target.checked }); } }} disabled={!!patchOptions.tools || allDisabled} />}
              label={
                <Tooltip title="Cluster Verification Utility — Available from AutoUpgrade 26.3+" placement="right" arrow componentsProps={{ tooltip: { sx: { bgcolor: theme => theme.palette.mode === 'dark' ? '#f5f5f5' : '#23272f', color: theme => theme.palette.mode === 'dark' ? '#222' : '#fff', fontSize: 15, borderRadius: 2, boxShadow: 3, px: 2, py: 1 } } }}>
                  <Typography component="span" sx={{ fontWeight: 600, ...(!!patchOptions.tools && { color: 'text.disabled' }) }}>CVU</Typography>
                </Tooltip>
              }
            />
          ) : (
            <Tooltip
              title={`Cluster Verification Utility — only available from AutoUpgrade ${featureRequiredVersion('patch.cvu')}+`}
              placement="right"
              arrow
              componentsProps={{ tooltip: { sx: { bgcolor: theme => theme.palette.mode === 'dark' ? '#f5f5f5' : '#23272f', color: theme => theme.palette.mode === 'dark' ? '#222' : '#fff', fontSize: 15, borderRadius: 2, boxShadow: 3, px: 2, py: 1, maxWidth: 320 } } }}
            >
              <Box sx={{ opacity: 0.38, cursor: 'not-allowed', display: 'inline-flex', alignItems: 'center', width: 'fit-content' }}>
                <Box sx={{ pointerEvents: 'none' }}>
                  <FormControlLabel control={<Checkbox checked={false} disabled />} label={<Typography component="span" sx={{ fontWeight: 600 }}>CVU</Typography>} />
                </Box>
              </Box>
            </Tooltip>
          )}
          {feat('patch.sqlcl') ? (
            <FormControlLabel
              control={
                <Checkbox
                  checked={!oemSelected && (!!patchOptions.tools || !!patchOptions.sqlcl)}
                  onChange={e => { if (e.target.checked && executionMode !== 'download') { setDownloadModeConfirm('sqlcl'); } else { setPatchOptions({ ...patchOptions, sqlcl: e.target.checked }); } }}
                  disabled={!!patchOptions.tools || allDisabled}
                />
              }
              label={
                <Tooltip
                  title="SQL Developer Command Line — Available from AutoUpgrade 26.4+"
                  placement="right"
                  arrow
                  componentsProps={{
                    tooltip: {
                      sx: {
                        bgcolor: theme => theme.palette.mode === 'dark' ? '#f5f5f5' : '#23272f',
                        color: theme => theme.palette.mode === 'dark' ? '#222' : '#fff',
                        fontSize: 15,
                      },
                    },
                  }}
                >
                  <Typography component="span" sx={{ fontWeight: 600 }}>SQLcl</Typography>
                </Tooltip>
              }
            />
          ) : (
            <Tooltip
              title={`SQL Developer Command Line — only available from AutoUpgrade ${featureRequiredVersion('patch.sqlcl')}+`}
              placement="right"
              arrow
              componentsProps={{ tooltip: { sx: { bgcolor: theme => theme.palette.mode === 'dark' ? '#f5f5f5' : '#23272f', color: theme => theme.palette.mode === 'dark' ? '#222' : '#fff', fontSize: 15, borderRadius: 2, boxShadow: 3, px: 2, py: 1, maxWidth: 320 } } }}
            >
              <Box sx={{ opacity: 0.38, cursor: 'not-allowed', display: 'inline-flex', alignItems: 'center', width: 'fit-content' }}>
                <Box sx={{ pointerEvents: 'none' }}>
                  <FormControlLabel
                    control={<Checkbox checked={false} disabled />}
                    label={<Typography component="span" sx={{ fontWeight: 600 }}>SQLcl</Typography>}
                  />
                </Box>
              </Box>
            </Tooltip>
          )}
          {/* DBSAT — 26.6+, download-only */}
          {feat('patch.dbsat') && (
            <Box>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={!oemSelected && !!patchOptions.dbsat}
                    disabled={allDisabled}
                    onChange={e => {
                      if (e.target.checked && executionMode !== 'download') {
                        setDownloadModeConfirm('dbsat');
                      } else {
                        setPatchOptions({ ...patchOptions, dbsat: e.target.checked });
                      }
                    }}
                  />
                }
                label={
                  <Tooltip
                    title={executionMode !== 'download' ? 'DB Security Assessment Tool — requires Download mode. Click to switch.' : 'DB Security Assessment Tool. AutoUpgrade 26.6+'}
                    placement="right" arrow
                    componentsProps={{ tooltip: { sx: { bgcolor: theme => theme.palette.mode === 'dark' ? '#f5f5f5' : '#23272f', color: theme => theme.palette.mode === 'dark' ? '#222' : '#fff', fontSize: 15, borderRadius: 2, boxShadow: 3, px: 2, py: 1, maxWidth: 300 } } }}
                  >
                    <Typography component="span" sx={{ fontWeight: 600 }}>DBSAT</Typography>
                  </Tooltip>
                }
              />
            </Box>
          )}
          {/* EXAPATCHMGR — 26.6+, download-only */}
          {feat('patch.exapatchmgr') && (
            <Box>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={!oemSelected && !!patchOptions.exapatchmgr}
                    disabled={allDisabled}
                    onChange={e => {
                      if (e.target.checked && executionMode !== 'download') {
                        setDownloadModeConfirm('exapatchmgr');
                      } else {
                        setPatchOptions({ ...patchOptions, exapatchmgr: e.target.checked });
                      }
                    }}
                  />
                }
                label={
                  <Tooltip
                    title={executionMode !== 'download' ? 'Exadata Patch Manager — requires Download mode. Click to switch.' : 'Exadata Patch Manager. AutoUpgrade 26.6+'}
                    placement="right" arrow
                    componentsProps={{ tooltip: { sx: { bgcolor: theme => theme.palette.mode === 'dark' ? '#f5f5f5' : '#23272f', color: theme => theme.palette.mode === 'dark' ? '#222' : '#fff', fontSize: 15, borderRadius: 2, boxShadow: 3, px: 2, py: 1, maxWidth: 300 } } }}
                  >
                    <Typography component="span" sx={{ fontWeight: 600 }}>EXAPATCHMGR</Typography>
                  </Tooltip>
                }
              />
            </Box>
          )}
          {/* OEM — 26.6+, download-only, exclusive */}
          {feat('patch.oem') && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={!!patchOptions.oem}
                    disabled={patchOptions.recommended || !!patchOptions.useGoldImageFile || giSelected}
                    onChange={e => {
                      if (e.target.checked && executionMode !== 'download') {
                        setDownloadModeConfirm('oem');
                      } else if (e.target.checked) {
                        setPatchOptions({
                          ...patchOptions, oem: true,
                          ru: false, ojvm: false, dpbp: false, ocw: false, cspu: false,
                          cpat: false, dbsat: false, exaqfsdp: false, exapatchmgr: false,
                          gi: false,
                          text: false, opatch: false, au: false, ahf: false, cvu: false,
                          sqlcl: false, tools: false, sdo: false, mrp: false, jdk: false,
                          recommended: false,
                        });
                      } else {
                        setPatchOptions({ ...patchOptions, oem: false, oemVersion: '' });
                      }
                    }}
                  />
                }
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Tooltip
                      title={executionMode !== 'download' ? 'Oracle Enterprise Manager — requires Download mode. Exclusive: cannot combine with other patch options. Click to switch.' : 'Oracle Enterprise Manager. Exclusive — cannot combine with other patch options. AutoUpgrade 26.6+'}
                      placement="right" arrow
                      componentsProps={{ tooltip: { sx: { bgcolor: theme => theme.palette.mode === 'dark' ? '#f5f5f5' : '#23272f', color: theme => theme.palette.mode === 'dark' ? '#222' : '#fff', fontSize: 15, borderRadius: 2, boxShadow: 3, px: 2, py: 1, maxWidth: 360 } } }}
                    >
                      <Typography component="span" sx={{ fontWeight: 600 }}>OEM</Typography>
                    </Tooltip>
                    <InfoTooltip
                      title={<>Oracle Enterprise Manager — <b>exclusive</b>: cannot combine with other patch options. Download mode only. AutoUpgrade 26.6+.</>}
                      placement="right"
                      disabled={patchOptions.recommended || !!patchOptions.useGoldImageFile || giSelected}
                    />
                  </Box>
                }
              />
              {!!patchOptions.oem && (
                <Select
                  size="small"
                  value={patchOptions.oemVersion || ''}
                  onChange={e => setPatchOptions({ ...patchOptions, oemVersion: e.target.value as string })}
                  displayEmpty
                  sx={{ minWidth: 130 }}
                >
                  <MenuItem value="" disabled><em>Version</em></MenuItem>
                  <MenuItem value="13.5">13.5 (OEM 13c)</MenuItem>
                  <MenuItem value="24.1">24.1 (OEM 24ai)</MenuItem>
                </Select>
              )}
            </Box>
          )}
        </Box>
      </Box>

      {/* GOLDIMAGE — deploy/create_home only */}
      {feat('patch.goldimage_mode') && (executionMode === 'deploy' || executionMode === 'create_home') && (
        <Box sx={{ mt: 1 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={!oemSelected && !!patchOptions.useGoldImageFile}
                onChange={e => setPatchOptions({ ...patchOptions, useGoldImageFile: e.target.checked, goldImageFile: patchOptions.goldImageFile || '', ...(e.target.checked && { createGoldImage: '' }) })}
                disabled={oemSelected}
              />
            }
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography sx={{ fontWeight: 600 }}>GOLDIMAGE</Typography>
                <InfoTooltip
                  title={<>Apply a user-supplied local <code>Gold_Image.zip</code> instead of downloading patches. Exclusive — cannot combine with other patch options. AutoUpgrade 26.5+.</>}
                  placement="right"
                  disabled={oemSelected}
                />
              </Box>
            }
          />
          {patchOptions.useGoldImageFile && (
            <TextField
              size="small"
              placeholder={`${patchFolder || patchFolderPlaceholder}/my_image_${optionalVersionPlaceholder}.zip`}
              value={patchOptions.goldImageFile || ''}
              onChange={e => setPatchOptions({ ...patchOptions, goldImageFile: e.target.value })}
              fullWidth
              sx={{ mt: 0.5 }}
            />
          )}
        </Box>
      )}

      {/* Confirmation dialog: switching to Download mode for CPAT/DBSAT/EXAQFSDP/EXAPATCHMGR/OEM/GI */}
      <Dialog open={!!downloadModeConfirm} onClose={() => setDownloadModeConfirm(null)}>
        <DialogTitle>Switch to Download mode?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            <strong>{downloadModeConfirm?.toUpperCase()}</strong> is only available in Download mode.
            Switching will change the Execution Mode to <strong>Download</strong> and enable this option.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDownloadModeConfirm(null)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => {
              const field = downloadModeConfirm!;
              setDownloadModeConfirm(null);
              if (field === 'oem') {
                // OEM is exclusive — clear all other patch options, default version to 24.1
                setPatchOptions({
                  ...patchOptions,
                  oem: true,
                  oemVersion: patchOptions.oemVersion || '24.1',
                  ru: false, ojvm: false, dpbp: false, ocw: false, cspu: false,
                  cpat: false, dbsat: false, exaqfsdp: false, exapatchmgr: false,
                  gi: false,
                  text: false, opatch: false, au: false, ahf: false, cvu: false,
                  sqlcl: false, tools: false, sdo: false, mrp: false, jdk: false,
                  recommended: false,
                });
              } else if (field === 'gi') {
                // GI clears most options but allows MRP and OPATCH
                setPatchOptions({
                  ...patchOptions,
                  gi: true,
                  ru: false, ojvm: false, dpbp: false, ocw: false, cspu: false,
                  cpat: false, dbsat: false, exaqfsdp: false, exapatchmgr: false,
                  oem: false, oemVersion: '',
                  text: false, au: false, ahf: false, cvu: false,
                  sqlcl: false, tools: false, sdo: false, jdk: false,
                  recommended: false,
                });
              } else {
                setPatchOptions({ ...patchOptions, [field]: true });
              }
              onSwitchToDownloadMode?.();
              onNavigateToPatchTab?.();
            }}
          >
            Switch to Download
          </Button>
        </DialogActions>
      </Dialog>

      {mrpVersionWarning && (
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 2, ml: 1, border: '2px solid', borderColor: 'error.main', borderRadius: 2, p: 2, background: theme => theme.palette.mode === 'dark' ? '#2a2323' : '#fff0f0' }}>
          <Box sx={{ color: 'error.main', mt: '2px', mr: 1 }}>
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 2L2 20h20L12 2zm0 14v-4m0 8h.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </Box>
          <Typography sx={{ color: 'error.main', fontSize: 15, fontWeight: 500 }}>
            The patch parameter for prefix <b>{patchKey || 'patch1'}</b> is invalid. Monthly Recommended Patches only exist for RU versions starting 19.17
          </Typography>
        </Box>
      )}
      {feat('create_gold_image') && (executionMode === 'deploy' || executionMode === 'create_home') && (
        <Box sx={{ mt: 1 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={!!patchOptions.createGoldImage}
                disabled={!!patchOptions.useGoldImageFile}
                onChange={e => setPatchOptions({ ...patchOptions, createGoldImage: e.target.checked ? 'YES' : '' })}
              />
            }
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography sx={{ fontWeight: 600 }}>Create Gold Image</Typography>
                <InfoTooltip
                  title={<>After patching completes, AutoUpgrade creates a Gold Image <code>.zip</code> using its default filename template (<code>db_goldimage_%RELEASE%_%UPDATE%_%TIMESTAMP%.zip</code>). Optionally specify a custom template using placeholders: <b>%RELEASE%</b>, <b>%UPDATE%</b>, <b>%DATE%</b>, <b>%TIMESTAMP%</b>. Cannot be combined with <b>GOLDIMAGE</b> patch mode. AutoUpgrade 26.5+.</>}
                  placement="top"
                  disabled={!!patchOptions.useGoldImageFile}
                />
              </Box>
            }
          />
          {!!patchOptions.createGoldImage && (
            <Box sx={{ mt: 0.5 }}>
              {executionMode === 'deploy' && (
                <Box sx={{ mb: 1, background: theme => theme.palette.mode === 'dark' ? '#2a2a1a' : '#fffde7', border: '1px solid', borderColor: theme => theme.palette.mode === 'dark' ? '#665500' : '#f9a825', borderRadius: 2, px: 2, py: 1 }}>
                  <Typography sx={{ fontSize: 13, color: theme => theme.palette.mode === 'dark' ? '#ffe082' : '#5d4037', lineHeight: 1.5 }}>
                    Gold Image creation in Deploy mode requires AutoUpgrade to create the Oracle Home as part of this operation — the Target Home must <b>not</b> be pre-created.
                  </Typography>
                </Box>
              )}
              <TextField
                size="small"
                fullWidth
                placeholder="db_goldimage_%RELEASE%_%UPDATE%_%TIMESTAMP%"
                value={patchOptions.createGoldImage === 'YES' ? '' : (patchOptions.createGoldImage || '').replace(/\.zip$/, '')}
                onChange={e => {
                  const typed = e.target.value.replace(/\.zip$/i, '');
                  setPatchOptions({ ...patchOptions, createGoldImage: typed ? typed + '.zip' : 'YES' });
                }}
                disabled={!!patchOptions.useGoldImageFile}
                slotProps={{
                  input: {
                    endAdornment: (
                      <InputAdornment position="end">
                        <Typography sx={{ color: 'text.disabled', fontSize: 13, userSelect: 'none' }}>.zip</Typography>
                      </InputAdornment>
                    ),
                    sx: {
                      '& input::placeholder': { color: '#8a8a8a', opacity: 1 },
                      fontSize: 15,
                    },
                  },
                  htmlInput: { ref: goldImageInputRef },
                }}
                helperText={
                  <Typography component="span" sx={{ fontSize: 11, color: 'text.disabled', fontStyle: 'italic' }}>
                    {patchOptions.createGoldImage === 'YES'
                      ? 'Leave empty to use AutoUpgrade\'s default filename template.'
                      : 'Custom filename will be used.'}
                  </Typography>
                }
              />
              <Box sx={{ display: 'flex', gap: 0.75, mt: 0.25, flexWrap: 'wrap', alignItems: 'center' }}>
                <Typography sx={{ fontSize: 11, color: 'text.disabled' }}>Placeholders:</Typography>
                {(['%RELEASE%', '%UPDATE%', '%DATE%', '%TIMESTAMP%'] as const).map(ph => {
                  const effectiveVal = patchOptions.createGoldImage === 'YES'
                    ? 'db_goldimage_%RELEASE%_%UPDATE%_%TIMESTAMP%.zip'
                    : (patchOptions.createGoldImage || '');
                  const active = effectiveVal.includes(ph);
                  return (
                    <Chip
                      key={ph}
                      label={ph}
                      size="small"
                      variant={active ? 'filled' : 'outlined'}
                      disabled={!!patchOptions.useGoldImageFile}
                      onClick={() => {
                        const raw = patchOptions.createGoldImage === 'YES'
                          ? 'db_goldimage_%RELEASE%_%UPDATE%_%TIMESTAMP%.zip'
                          : (patchOptions.createGoldImage || 'db_goldimage_.zip');
                        const current = raw.endsWith('.zip') ? raw : raw + '.zip';
                        let next: string;
                        if (current.includes(ph)) {
                          next = current.replace(ph, '')
                            .replace(/__+/g, '_')
                            .replace(/_\./g, '.')
                            .replace(/^_/, '')
                            .replace(/_$/, '');
                        } else {
                          next = current.slice(0, -4) + '_' + ph + '.zip';
                          next = next.replace(/__+/g, '_');
                        }
                        setPatchOptions({ ...patchOptions, createGoldImage: next || 'YES' });
                        setTimeout(() => {
                          const el = goldImageInputRef.current as HTMLInputElement | null;
                          if (el) { el.focus(); el.setSelectionRange(el.value.length, el.value.length); }
                        }, 0);
                      }}
                      sx={{
                        fontSize: 11,
                        fontFamily: 'monospace',
                        height: 22,
                        cursor: 'pointer',
                        ...(active ? {
                          background: (theme: any) => theme.palette.mode === 'dark' ? '#1a3a1a' : '#e8f5e9',
                          color: (theme: any) => theme.palette.mode === 'dark' ? '#81c784' : '#2e7d32',
                          borderColor: '#4caf50',
                          '&:hover': {
                            background: (theme: any) => theme.palette.mode === 'dark' ? '#1f4a1f' : '#c8e6c9',
                          },
                        } : {
                          '&:hover': {
                            background: (theme: any) => theme.palette.mode === 'dark' ? '#1a2a1a' : '#f1f8e9',
                          },
                        }),
                      }}
                    />
                  );
                })}
              </Box>
            </Box>
          )}
        </Box>
      )}
      <Box sx={{ mt: 2 }}>
        <Typography sx={{ fontWeight: 600, mb: 1 }}>Specific Patch(es):</Typography>
        <TextField
          fullWidth
          size="small"
          placeholder="35197819,<patch2>,<patch3>"
          value={patchOptions.specificPatches}
          onChange={e => { setSpecificPatchesTouched(true); handleInput('specificPatches')(e); }}
          onFocus={e => { if (!specificPatchesTouched && patchOptions.specificPatches === '') handleInput('specificPatches')(e); setSpecificPatchesTouched(true); }}
          InputProps={{
            sx: {
              '& input::placeholder': { color: '#8a8a8a', opacity: 1 },
              '& .MuiInputBase-input': {
                color: !specificPatchesTouched && patchOptions.specificPatches === '' ? '#8a8a8a' : theme => theme.palette.mode === 'dark' ? '#fff' : '#000',
              },
              fontSize: 15,
              background: 'transparent',
            },
          }}
        />
      </Box>
      <Box sx={{ mt: 2 }}>
        <Typography sx={{ fontWeight: 600, mb: 1 }}>Download Folder:</Typography>
        <TextField
          fullWidth
          size="small"
          value={patchFolder}
          onChange={e => { setPatchFolderTouched(true); setPatchFolder(e.target.value); }}
          onFocus={() => { if (!patchFolderTouched && patchFolder === '') setPatchFolder(''); setPatchFolderTouched(true); }}
          placeholder={patchFolderPlaceholder}
          InputProps={{
            sx: {
              '& input::placeholder': { color: '#8a8a8a', opacity: 1 },
              '& .MuiInputBase-input': {
                color: !patchFolderTouched && patchFolder === '' ? '#8a8a8a' : theme => theme.palette.mode === 'dark' ? '#fff' : '#000',
              },
              fontSize: 15,
              background: 'transparent',
            },
          }}
        />
      </Box>
      {feat('gold_image') && ['download', 'deploy', 'analyze'].includes(executionMode ?? '') && (
        <Box sx={{ mt: 3 }}>
          {(executionMode === 'deploy' || executionMode === 'analyze') && download === 'NO' && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                flexWrap: 'wrap',
                minWidth: 0,
                background: theme => theme.palette.mode === 'dark' ? '#333' : '#e3f2fd',
                color: theme => theme.palette.mode === 'dark' ? '#b2ebf2' : '#1976d2',
                borderRadius: 2,
                px: 2,
                py: 1,
                mb: 2,
                fontWeight: 500,
                fontSize: 15,
                maxWidth: '100%',
                gap: 1.5,
              }}
            >
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 0, flex: 1 }}>
                To change Gold Image settings, enable Download in Download Settings tab
              </span>
            </Box>
          )}
          <Box sx={{ ...((executionMode === 'deploy' || executionMode === 'analyze') && download === 'NO' && { opacity: 0.6 }) }}>
          <FormLabel component="legend" sx={{ fontWeight: 600, mb: 1, display: 'block' }}>
            <Tooltip
              title={
                <Typography sx={{ fontSize: 16, lineHeight: 1.5 }}>
                  <Box sx={{ pt: 1 }}>
                    <Typography sx={{ mb: 1 }}>&nbsp;</Typography>
                    <Typography sx={{ fontWeight: 600, mb: 1 }}>NO:</Typography>
                    <Typography sx={{ mb: 1.5, fontSize: 15 }}>
                      Gold Image will not be used. This maintains the previous behavior where patches are individually downloaded from MOS and applied. Note: For releases prior to 26ai, the base release must be placed in the patches folder.
                    </Typography>
                    <Typography sx={{ fontWeight: 600, mb: 1 }}>YES:</Typography>
                    <Typography sx={{ mb: 1.5, fontSize: 15 }}>
                      The Gold Image feature is enabled. When this value is provided, AU will request and download the generated Gold Image for all patches from Oracle Updater Service, except for anything that is a number in the patch parameter (one-offs). So, the one-off patches are still downloaded and applied individually from MOS. This option will require that the MOS CSI be loaded into the keystore.
                    </Typography>
                    <Typography sx={{ fontWeight: 600, mb: 1 }}>ALL:</Typography>
                    <Typography sx={{ mb: 1.5, fontSize: 15 }}>
                      The Gold Image feature is enabled. Differently from YES, if ALL is provided, AU will download the generated Gold Image for all patches from Oracle Updater Service. This option will require that the MOS CSI be loaded into the keystore.
                    </Typography>
                    <Typography sx={{ fontWeight: 600, mb: 1 }}>AUTO (default):</Typography>
                    <Typography sx={{ mb: 1.5, fontSize: 15 }}>
                      The default value of AUTO will automatically decide the best approach, selecting between YES or NO. If all of these preconditions are met, then AUTO is resolved as YES. Otherwise, AUTO is resolved as NO:
                    </Typography>
                    <ul style={{ marginTop: 0, marginBottom: 12, paddingLeft: 20, fontSize: 15 }}>
                      <li>A valid CSI with My Oracle Support credentials is loaded into the AutoUpgrade Patching keystore</li>
                      <li>Connection to Oracle Updater is successful (if you are behind a firewall, https://transport.oracle.com/ needs to be whitelisted)</li>
                      <li>The requested platform is LINUX.X64</li>
                      <li>The requested RU is available on the Oracle Updater service (the service only provides the 3 latest RUs)</li>
                    </ul>
                    <Typography sx={{ mt: 1 }}>&nbsp;</Typography>
                  </Box>
                </Typography>
              }
              placement="right"
              arrow
              componentsProps={{
                tooltip: {
                  sx: {
                    backgroundColor: theme => theme.palette.mode === 'dark' ? '#f5f5f5' : '#23272f',
                    color: theme => theme.palette.mode === 'dark' ? '#222' : '#fff',
                    borderRadius: 8,
                    fontSize: 16,
                    py: 2.2,
                    px: 4,
                    boxShadow: 3,
                    maxWidth: 1100,
                    minWidth: 220,
                  },
                },
                arrow: {
                  sx: {
                    color: theme => theme.palette.mode === 'dark' ? '#f5f5f5' : '#23272f',
                  },
                },
              }}
            >
              <Typography sx={{ fontWeight: 600, display: 'inline', cursor: 'help' }}>Gold Image:</Typography>
            </Tooltip>
          </FormLabel>
          <Select
            size="small"
            value={patchOptions.goldImage || 'AUTO'}
            onChange={e => setPatchOptions({ ...patchOptions, goldImage: e.target.value as 'NO' | 'YES' | 'ALL' | 'AUTO', goldImageSecurityLevel: undefined })}
            disabled={(executionMode === 'deploy' || executionMode === 'analyze') && download === 'NO'}
            sx={{ width: 120 }}
          >
            <MenuItem value="NO">No</MenuItem>
            <MenuItem value="YES">Yes</MenuItem>
            <MenuItem value="ALL">All</MenuItem>
            <MenuItem value="AUTO">Auto</MenuItem>
          </Select>
          {feat('gold_image.security_patch_level') && (patchOptions.goldImage === 'YES' || patchOptions.goldImage === 'ALL') && (
            <Box sx={{ mt: 1.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.75 }}>
                <Typography sx={{ fontWeight: 600, fontSize: 14 }}>Security Patch Level:</Typography>
                <Tooltip
                  arrow
                  placement="right"
                  componentsProps={{
                    tooltip: {
                      sx: {
                        backgroundColor: theme => theme.palette.mode === 'dark' ? '#f5f5f5' : '#23272f',
                        color: theme => theme.palette.mode === 'dark' ? '#222' : '#e0e0e0',
                        borderRadius: 3,
                        py: 1.5,
                        px: 2,
                        boxShadow: 6,
                        maxWidth: 480,
                      },
                    },
                    arrow: { sx: { color: theme => theme.palette.mode === 'dark' ? '#f5f5f5' : '#23272f' } },
                  }}
                  title={
                    <Box>
                      <Typography sx={{ fontSize: 11, mb: 1.5, opacity: 0.75, lineHeight: 1.4 }}>
                        Filters Gold Image downloads by minimum security level via OUA.
                      </Typography>
                      <Box sx={{ display: 'grid', gridTemplateColumns: 'auto auto 1fr', gap: '4px 12px', alignItems: 'center' }}>
                        {([
                          { level: 'CRITICAL', color: '#e53935', cvss: '≥ 9.0', desc: 'Critical vulnerabilities only' },
                          { level: 'HIGH',     color: '#e64a19', cvss: '≥ 7.0', desc: 'High + Critical  ★ AU default' },
                          { level: 'MEDIUM',   color: '#b08800', cvss: '≥ 4.0', desc: 'Medium, High + Critical' },
                          { level: 'LOW',      color: '#66bb6a', cvss: '≥ 0.1', desc: 'All positive CVSS fixes' },
                        ] as const).map(row => (
                          <React.Fragment key={row.level}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
                              <Box sx={{ width: 8, height: 8, borderRadius: '50%', background: row.color, flexShrink: 0 }} />
                              <Typography sx={{ fontFamily: 'monospace', fontSize: 11, fontWeight: 700, color: row.color }}>{row.level}</Typography>
                            </Box>
                            <Typography sx={{ fontSize: 11, opacity: 0.6, whiteSpace: 'nowrap' }}>CVSS {row.cvss}</Typography>
                            <Typography sx={{ fontSize: 11, opacity: 0.85 }}>{row.desc}</Typography>
                          </React.Fragment>
                        ))}
                      </Box>
                      <Typography sx={{ fontSize: 10, mt: 1.5, opacity: 0.5, fontStyle: 'italic' }}>
                        Valid only for AutoUpgrade 26.5+
                      </Typography>
                    </Box>
                  }
                >
                  <InfoOutlinedIcon color="primary" sx={{ fontSize: 18, cursor: 'pointer' }} />
                </Tooltip>
              </Box>
              <Select
                size="small"
                displayEmpty
                value={patchOptions.goldImageSecurityLevel || 'HIGH'}
                onChange={e => setPatchOptions({ ...patchOptions, goldImageSecurityLevel: e.target.value as 'CRITICAL' | 'HIGH' | 'MEDIUM' | 'LOW' })}
                disabled={(executionMode === 'deploy' || executionMode === 'analyze') && download === 'NO'}
                sx={{ width: 160 }}
              >
                <MenuItem value="CRITICAL">CRITICAL</MenuItem>
                <MenuItem value="HIGH">HIGH</MenuItem>
                <MenuItem value="MEDIUM">MEDIUM</MenuItem>
                <MenuItem value="LOW">LOW</MenuItem>
              </Select>
            </Box>
          )}
          </Box>
        </Box>
      )}
    </Paper>
  );
};

export default PatchContent; 